import { useState } from "react";
import { CheckCircle2, Circle, Clock3, XCircle } from "lucide-react";
import { Badge, Button, Field } from "@studio";
import { KeywordInput } from "@studio-features/meta-crew/seo-shared";
import { api, errorMessage } from "../lib/api";
import { fmtDateTime, fmtTime, stageNames } from "../lib/format";
import { keys, useAction, useJobComments } from "../lib/queries";
import type { RunDetail, TaskDetail } from "../lib/types";
import { InfoRow, Loading, Panel, PanelError } from "../console/ui";

/**
 * 고객 발행 요청 (2026-10-16). 전용 시안이 아직 없어 콘솔 부품으로 임시 구성했다.
 * - PublishTimeline: 접수 → 확인 중 → 발행 대기 → 원고 검토 → 작업 수행 → 작업 완료 → 완료 검토 → 발행됨
 * - ThreadPanel: 고객·운영팀·시스템 댓글 (읽으면 읽음 처리)
 * - ApprovalPanel: 직원의 승인(키워드·실행 시각) / 수정 요청 / 반려
 */

const STEPS: [key: string, label: string][] = [
  ["received", "접수"],
  ["review", "확인 중"],
  ["waiting", "발행 대기"],
  ["s_review", stageNames.review!],
  ["s_run", stageNames.run!],
  ["s_posted", stageNames.posted!],
  ["s_verify", stageNames.verify!],
  ["done", "발행됨"],
];
const STAGE_INDEX: Record<string, number> = { review: 3, run: 4, posted: 5, verify: 6, done: 7 };

/** 현재 단계 번호와 실패 여부. 승인 모드가 auto 면 "확인 중"을 건너뛴 것으로 본다. */
function progressOf(r: RunDetail, t: TaskDetail | null | undefined): { current: number; failed: boolean; stopped: boolean } {
  if (r.status === "succeeded") return { current: 7, failed: false, stopped: false };
  const failed = r.status === "failed" || r.status === "dead";
  const stopped = r.status === "cancelled";
  if (r.status === "held") {
    if (r.errorKind === "approval_required" || r.errorKind === "changes_requested") return { current: 1, failed: false, stopped: false };
    return { current: 2, failed: false, stopped: false };
  }
  const stage = (t?.result?.stage ?? r.stage) as string | null | undefined;
  if (stage && STAGE_INDEX[stage] !== undefined) return { current: STAGE_INDEX[stage]!, failed, stopped };
  if (t && ["assigned", "running"].includes(t.status)) return { current: 3, failed, stopped };
  if (failed || stopped) return { current: 2, failed, stopped };
  return { current: 2, failed: false, stopped: false };
}

export function PublishTimeline({ run, task }: { run: RunDetail; task: TaskDetail | null | undefined }) {
  const { current, failed, stopped } = progressOf(run, task);
  return (
    <ol className="mcc-publish-steps" aria-label="발행 진행">
      {STEPS.map(([key, label], i) => {
        const cls = i < current ? "done" : i === current ? (failed || stopped ? "failed" : "current") : "";
        const Icon = i < current ? CheckCircle2 : i === current ? (failed || stopped ? XCircle : Clock3) : Circle;
        return (
          <li key={key} className={cls}>
            <Icon size={13} />
            {label}
          </li>
        );
      })}
    </ol>
  );
}

/** 대화 스레드. canWrite 가 있으면 답글 입력. */
export function ThreadPanel({ customerId, jobId, canWrite }: { customerId: string; jobId: string; canWrite: boolean }) {
  const q = useJobComments(customerId, jobId);
  const [body, setBody] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const send = useAction((text: string) => api.post(`/c/${customerId}/jobs/${jobId}/comments`, { body: text }), [keys.comments(customerId, jobId), ["c", customerId], ["admin", "runs"], ["run"]]);
  const items = q.data?.items ?? [];
  return (
    <Panel title={`운영팀과 대화 · ${items.length}`}>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <PanelError error={errorMessage(q.error)} onRetry={() => void q.refetch()} />
      ) : (
        <div className="lwd-panel-body mcc-thread">
          {items.length === 0 && <InfoRow title="아직 대화가 없습니다" description="원고나 발행 일정에 대해 운영팀과 여기서 이야기합니다." />}
          {items.map((c) => (
            <div key={c.id} className={`mcc-thread-item${c.mine ? " mine" : ""}${c.authorKind === "system" ? " system" : ""}`}>
              <small>
                {c.authorKind === "system" ? "시스템" : c.authorKind === "staff" ? "운영팀" : (c.authorName ?? "고객")} · {fmtDateTime(c.createdAt)} · v{c.revision}
              </small>
              <p>{c.body}</p>
            </div>
          ))}
          {canWrite && (
            <form
              className="mcc-thread-form"
              onSubmit={(e) => {
                e.preventDefault();
                setErr(null);
                const text = body.trim();
                if (!text) return;
                send
                  .mutateAsync(text)
                  .then(() => setBody(""))
                  .catch((er) => setErr(errorMessage(er)));
              }}
            >
              <textarea className="input" aria-label="답글" value={body} onChange={(e) => setBody(e.target.value)} placeholder="답글을 입력하세요" maxLength={4000} />
              <Button type="submit" size="sm" disabled={send.isPending || !body.trim()}>
                보내기
              </Button>
            </form>
          )}
          {err && <small className="mcc-manuscript-error">{err}</small>}
        </div>
      )}
    </Panel>
  );
}

/** 직원: 확인 대기 발행 요청의 승인·수정 요청·반려. 승인은 /c 경로나 /admin 경로 둘 다 같은 API. */
export function ApprovalPanel({ base, run, visibility, invalidate, onDone }: { base: string; run: RunDetail; visibility?: string | null; invalidate: readonly (readonly unknown[])[]; onDone: (msg: string) => void }) {
  const [keywords, setKeywords] = useState<string[]>(run.keywords ?? []);
  const [runAt, setRunAt] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const approve = useAction((b: { keywords?: string[]; runAt?: string }) => api.post(`${base}/approve`, b), invalidate as never);
  const changes = useAction((r: string) => api.post(`${base}/request-changes`, { reason: r }), invalidate as never);
  const reject = useAction((r: string) => api.post(`${base}/reject`, { reason: r }), invalidate as never);
  const act = (p: Promise<unknown>, done: string) => {
    setErr(null);
    p.then(() => onDone(done)).catch((e) => setErr(errorMessage(e)));
  };
  const seoAllowed = !visibility || visibility === "public";
  const desired = run.jobRunAt && new Date(run.jobRunAt).getTime() > Date.now() ? run.jobRunAt : null;
  return (
    <Panel title="운영팀 확인" extra={<Badge tone={run.errorKind === "changes_requested" ? "coral" : "amber"}>{run.errorKind === "changes_requested" ? "수정 요청 중" : "확인 대기"}</Badge>}>
      <div className="lwd-panel-body mcc-form">
        <InfoRow title={`원고 v${run.revision ?? 1} · 첨부 ${run.attachmentCount ?? 0}개`} description={desired ? `고객 희망 일시 ${fmtDateTime(desired)}` : "희망 일시 없음 (승인하면 바로 실행)"} />
        {seoAllowed ? <KeywordInput value={keywords} onChange={setKeywords} /> : <p className="meta-crew-muted">전체 공개가 아닌 글이라 SEO 키워드를 붙이지 않습니다.</p>}
        <Field label="실행 시각 (비우면 희망 일시 또는 지금)" type="datetime-local" value={runAt} onChange={(e) => setRunAt(e.target.value)} />
        <div className="lwd-actions">
          <Button onClick={() => act(approve.mutateAsync({ ...(seoAllowed && keywords.length ? { keywords } : {}), ...(runAt ? { runAt: new Date(runAt).toISOString() } : {}) }), "승인했습니다.")} disabled={approve.isPending}>
            승인
          </Button>
        </div>
        <Field label="수정 요청 · 반려 사유 (고객에게 그대로 보입니다)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} />
        <div className="lwd-actions">
          <Button variant="secondary" disabled={!reason.trim() || changes.isPending} onClick={() => act(changes.mutateAsync(reason.trim()), "수정을 요청했습니다.")}>
            수정 요청
          </Button>
          <Button variant="ghost" disabled={!reason.trim() || reject.isPending} onClick={() => act(reject.mutateAsync(reason.trim()), "반려했습니다.")}>
            반려
          </Button>
        </div>
        {err && <small className="mcc-manuscript-error">{err}</small>}
      </div>
    </Panel>
  );
}

/** 고객 상세에서 보여 줄 결과 한 줄: 발행됨이면 요약, 실패면 고정 문구(서버가 바꿔 줌). */
export function publishResultText(r: RunDetail, t: TaskDetail | null | undefined): string | null {
  if (r.status === "succeeded") return t?.result?.summary ?? r.resultSummary ?? "발행을 마쳤습니다.";
  if (["failed", "dead", "cancelled", "held"].includes(r.status)) return r.errorMessage ?? null;
  return null;
}

export const fmtDesired = (s: string | null | undefined) => (s ? `예약 ${fmtTime(s)}` : "");
